import React, { useEffect, useState, useCallback } from 'react';
import { FormControl, Button, Grid, Paper, Typography, OutlinedInput, Box, FormLabel, FormHelperText, MenuItem, Select, Switch, FormControlLabel, Chip, Autocomplete, TextField, RadioGroup, Radio, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Divider, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import { postLabOrderRequest, resetPostLabOrder } from '../../../../redux/modules/laboratory/labOrder/slice';
import { getLabTestRequest } from '../../../../redux/modules/laboratory/labTest/slice';
import { getLabTestPanelRequest } from '../../../../redux/modules/laboratory/labTestPanel/slice';
import requestHandler from '../../../../utils/requestHandler';
import DiagnosisInput from '../../../../components/DiagnosisInput';
import AddPatientDialog from './AddPatientDialog';
import SignaturePad from '../../../../components/SignaturePad';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { formatDob } from '../../../../utils/dob';

const validationSchema = yup.object().shape({
    priority: yup.string().required("Priority is required"),
});

const LabOrderForm = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = location.pathname.split('/laboratory')[0];
    let { loading, postRes } = useSelector(state => state.labOrder);
    // Only orderable items appear in the order menu. Sub-analytes (CBC params,
    // lipid fractions, electrolytes resulted via a panel) and any test/panel not
    // on the price list are seeded isOrderable:false and must not be sold alone.
    const tests = useSelector(state => (state.labTest.getRes?.data || []).filter(t => t.isOrderable !== false));
    const panels = useSelector(state => (state.labTestPanel.getRes?.data || []).filter(p => p.isOrderable !== false));
    const [selectedTests, setSelectedTests] = useState([]);
    const [selectedPanels, setSelectedPanels] = useState([]);

    // Patient autocomplete state
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [patientOptions, setPatientOptions] = useState([]);
    const [patientSearching, setPatientSearching] = useState(false);
    const [patientInputValue, setPatientInputValue] = useState('');

    // Quick-create patient dialog
    const [addPatientOpen, setAddPatientOpen] = useState(false);

    // Eureka-style requisition flow flags (see HospitalConfig.features)
    const features = useSelector(state => state.hospitalConfig?.features || {});
    const requireDemographics = !!features['lis.requisitionRequiredFields'];
    const requireConsent = !!features['lis.orderConsent'];

    // Required requisition demographics — editable so missing values can be
    // filled at the desk; persisted back to the patient record on submit.
    const [patientAddress, setPatientAddress] = useState('');
    const [patientPhone, setPatientPhone] = useState('');

    // Review & consent dialog state
    const [reviewOpen, setReviewOpen] = useState(false);
    const [reviewData, setReviewData] = useState(null);
    const [consentSignature, setConsentSignature] = useState(null);
    const [consentSignedBy, setConsentSignedBy] = useState('');

    const { register, handleSubmit, control, watch, setValue, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: { priority: 'routine', orderType: 'walk-in', isFasting: false, diagnosis: '' }
    });
    const orderType = watch('orderType');

    useEffect(() => {
        dispatch(setMainHeading("New Lab Order"));
        dispatch(getLabTestRequest({ page: 1, limit: 500, searchQuery: '' }));
        dispatch(getLabTestPanelRequest({ page: 1, limit: 100, searchQuery: '' }));
    }, [dispatch]);

    useEffect(() => {
        if (postRes) {
            if (postRes?.success) {
                toast(postRes?.message);
                navigate(`${basePath}/laboratory/orders`);
            } else {
                toast(postRes?.message);
            }
            dispatch(resetPostLabOrder());
        }
    }, [postRes, dispatch, navigate, basePath]);

    // Debounced patient search
    const searchPatients = useCallback(async (query) => {
        if (!query || query.length < 2) {
            setPatientOptions([]);
            return;
        }
        setPatientSearching(true);
        try {
            const authToken = localStorage.getItem("item");
            const currentBranch = JSON.parse(localStorage.getItem("currentBranch") || "null");
            const res = await requestHandler(
                `${process.env.REACT_APP_SERVER_API}api/patient-search?q=${encodeURIComponent(query)}&limit=20`,
                { method: "GET", headers: { "Authorization": authToken, "Content-Type": "application/json", "X-Current-Branch": currentBranch?.id || "" } }
            );
            setPatientOptions(res?.data || []);
        } catch {
            setPatientOptions([]);
        } finally {
            setPatientSearching(false);
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (patientInputValue && !selectedPatient) {
                searchPatients(patientInputValue);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [patientInputValue, selectedPatient, searchPatients]);

    // Keep editable demographics in sync with the chosen patient
    useEffect(() => {
        setPatientAddress(selectedPatient?.address || '');
        setPatientPhone(selectedPatient?.contactNumber ? String(selectedPatient.contactNumber) : '');
    }, [selectedPatient]);

    const buildOrderPayload = (data) => ({
        ...data,
        patientId: selectedPatient._id,
        tests: selectedTests.map(t => t._id),
        panelIds: selectedPanels.map(p => p._id),
        ...(requireDemographics ? { patientAddress, patientPhone } : {}),
    });

    const onSubmit = (data) => {
        if (!selectedPatient) {
            toast("Please select a patient");
            return;
        }
        if (selectedTests.length === 0 && selectedPanels.length === 0) {
            toast("Please select at least one test or panel");
            return;
        }
        if (requireDemographics) {
            if (!patientAddress.trim()) { toast("Patient address is required"); return; }
            if (!patientPhone.trim()) { toast("Patient phone number is required"); return; }
            if (data.orderType === 'physician' && !data.orderingPhysicianPhone?.trim()) {
                toast("Requesting physician phone number is required");
                return;
            }
        }
        if (requireConsent) {
            // First consent: the patient reviews every filled field, the tests
            // and the total cost, then signs before the order is placed.
            setReviewData(data);
            setConsentSignature(null);
            setConsentSignedBy(`${selectedPatient.fName || ''} ${selectedPatient.lName || ''}`.trim());
            setReviewOpen(true);
            return;
        }
        dispatch(postLabOrderRequest(buildOrderPayload(data)));
    };

    const confirmConsentAndSubmit = () => {
        if (!consentSignature) { toast("Patient signature is required"); return; }
        if (!consentSignedBy.trim()) { toast("Name of signer is required"); return; }
        dispatch(postLabOrderRequest({
            ...buildOrderPayload(reviewData),
            demographicsConsent: {
                signedByName: consentSignedBy.trim(),
                signatureImage: consentSignature,
            },
        }));
        setReviewOpen(false);
    };

    // Calculate running total
    const testTotal = selectedTests.reduce((sum, t) => sum + (t.price || 0), 0);
    const panelTotal = selectedPanels.reduce((sum, p) => sum + (p.price || 0), 0);
    const orderTotal = testTotal + panelTotal;

    const formatCurrency = (amount) => `$${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return (
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            {/* Section 1: Patient Search */}
            <Paper sx={{ padding: 2, mt: 3 }} className="cardStyle">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" className="title">Patient</Typography>
                    <Button size="small" variant="outlined" startIcon={<PersonAddAlt1Icon />} onClick={() => setAddPatientOpen(true)}>
                        New Patient
                    </Button>
                </Box>
                <Autocomplete
                    options={patientOptions}
                    getOptionLabel={(option) => `${option.fName} ${option.lName} (${option.mrn || 'No MRN'})`}
                    filterOptions={(x) => x}
                    value={selectedPatient}
                    onChange={(e, newValue) => {
                        setSelectedPatient(newValue);
                        if (!newValue) setPatientOptions([]);
                    }}
                    onInputChange={(e, value, reason) => {
                        if (reason === 'input') setPatientInputValue(value);
                    }}
                    loading={patientSearching}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    renderOption={(props, option) => (
                        <li {...props} key={option._id}>
                            <Box>
                                <Typography variant="body2" fontWeight={600}>
                                    {option.fName} {option.lName}
                                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>({option.mrn || 'No MRN'})</Typography>
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {option.gender ? `${option.gender.charAt(0).toUpperCase() + option.gender.slice(1)}` : ''}
                                    {option.dob ? ` | DOB: ${formatDob(option.dob)}` : ''}
                                </Typography>
                            </Box>
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Search by patient name or MRN"
                            size="small"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {patientSearching ? <CircularProgress size={18} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    noOptionsText={patientInputValue.length < 2 ? "Type at least 2 characters" : "No patients found"}
                />
                {/* Selected patient card */}
                {selectedPatient && (
                    <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5, bgcolor: 'action.hover' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={3}>
                                <Typography variant="caption" color="text.secondary">Name</Typography>
                                <Typography variant="body2" fontWeight={600}>{selectedPatient.fName} {selectedPatient.lName}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <Typography variant="caption" color="text.secondary">MRN</Typography>
                                <Typography variant="body2">{selectedPatient.mrn || '-'}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <Typography variant="caption" color="text.secondary">Gender</Typography>
                                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{selectedPatient.gender || '-'}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <Typography variant="caption" color="text.secondary">DOB</Typography>
                                <Typography variant="body2">{formatDob(selectedPatient.dob)}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary">Contact</Typography>
                                <Typography variant="body2">{selectedPatient.contactNumber || selectedPatient.email || '-'}</Typography>
                            </Grid>
                            {requireDemographics && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <FormLabel>Patient Address *</FormLabel>
                                            <OutlinedInput
                                                className="inputfield" size="small" placeholder="Street, city, region"
                                                value={patientAddress} onChange={(e) => setPatientAddress(e.target.value)}
                                            />
                                            {!patientAddress.trim() && <FormHelperText error>Required on the requisition form</FormHelperText>}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <FormLabel>Patient Phone Number *</FormLabel>
                                            <OutlinedInput
                                                className="inputfield" size="small" placeholder="Phone number"
                                                value={patientPhone} onChange={(e) => setPatientPhone(e.target.value)}
                                            />
                                            {!patientPhone.trim() && <FormHelperText error>Required on the requisition form</FormHelperText>}
                                        </FormControl>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Paper>
                )}
            </Paper>

            {/* Section 2: Order Details */}
            <Paper sx={{ padding: 2, mt: 3 }} className="cardStyle">
                <Typography variant="h6" className="title" sx={{ mb: 1 }}>Order Details</Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Priority</FormLabel>
                            <Controller name="priority" control={control} render={({ field }) => (
                                <RadioGroup row {...field}>
                                    <FormControlLabel value="routine" control={<Radio size="small" />} label="Routine" />
                                    <FormControlLabel value="urgent" control={<Radio size="small" color="warning" />} label="Urgent" />
                                    <FormControlLabel value="stat" control={<Radio size="small" color="error" />} label="STAT" />
                                </RadioGroup>
                            )} />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Order Type</FormLabel>
                            <Controller name="orderType" control={control} render={({ field }) => (
                                <RadioGroup row {...field}>
                                    <FormControlLabel value="walk-in" control={<Radio size="small" />} label="Walk-in" />
                                    <FormControlLabel value="physician" control={<Radio size="small" />} label="Physician" />
                                    <FormControlLabel value="emergency" control={<Radio size="small" />} label="Emergency" />
                                    <FormControlLabel value="referral" control={<Radio size="small" />} label="Referral" />
                                </RadioGroup>
                            )} />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <Controller name="isFasting" control={control} render={({ field }) => (
                            <FormControlLabel control={<Switch checked={field.value} onChange={field.onChange} />} label="Fasting" />
                        )} />
                    </Grid>
                </Grid>
            </Paper>

            {/* Section 3: Clinical Info */}
            <Paper sx={{ padding: 2, mt: 3 }} className="cardStyle">
                <Typography variant="h6" className="title" sx={{ mb: 1 }}>Clinical Information</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Ordering Physician</FormLabel>
                            <OutlinedInput className="inputfield" {...register('orderingPhysicianName')} size="small" placeholder="Dr. ..." />
                        </FormControl>
                    </Grid>
                    {orderType === 'physician' && (
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth>
                                <FormLabel>Physician Phone{requireDemographics ? ' *' : ''}</FormLabel>
                                <OutlinedInput className="inputfield" {...register('orderingPhysicianPhone')} size="small" placeholder="Requesting physician phone" />
                                {requireDemographics && <FormHelperText>Required when a physician requested the exam</FormHelperText>}
                            </FormControl>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Diagnosis / ICD-11</FormLabel>
                            <DiagnosisInput value={watch('diagnosis') || ''} onChange={(v) => setValue('diagnosis', v)} />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <FormLabel>Clinical Notes</FormLabel>
                            <OutlinedInput className="inputfield" {...register('clinicalNotes')} size="small" placeholder="Clinical notes" multiline rows={2} />
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {/* Section 4: Test Selection */}
            <Paper sx={{ padding: 2, mt: 3 }} className="cardStyle">
                <Typography variant="h6" className="title" sx={{ mb: 1 }}>Test Selection</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Individual Tests</Typography>
                        <Autocomplete
                            multiple
                            options={tests || []}
                            getOptionLabel={(option) => `${option.testCode} - ${option.name}`}
                            value={selectedTests}
                            onChange={(e, newValue) => setSelectedTests(newValue)}
                            renderInput={(params) => <TextField {...params} label="Search tests" size="small" />}
                            renderOption={(props, option) => (
                                <li {...props} key={option._id}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                        <Typography variant="body2">{option.testCode} - {option.name}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>{formatCurrency(option.price)}</Typography>
                                    </Box>
                                </li>
                            )}
                            renderTags={(value, getTagProps) => value.map((option, index) => (
                                <Chip variant="outlined" label={option.testCode} {...getTagProps({ index })} key={option._id} size="small" />
                            ))}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Panels</Typography>
                        <Autocomplete
                            multiple
                            options={panels || []}
                            getOptionLabel={(option) => `${option.panelCode} - ${option.name}`}
                            value={selectedPanels}
                            onChange={(e, newValue) => setSelectedPanels(newValue)}
                            renderInput={(params) => <TextField {...params} label="Search panels" size="small" />}
                            renderOption={(props, option) => (
                                <li {...props} key={option._id}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                        <Typography variant="body2">{option.panelCode} - {option.name}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>{formatCurrency(option.price)}</Typography>
                                    </Box>
                                </li>
                            )}
                            renderTags={(value, getTagProps) => value.map((option, index) => (
                                <Chip variant="outlined" color="primary" label={option.panelCode} {...getTagProps({ index })} key={option._id} size="small" />
                            ))}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Section 5: Order Summary */}
            {(selectedTests.length > 0 || selectedPanels.length > 0) && (
                <Paper sx={{ padding: 2, mt: 3 }} className="cardStyle">
                    <Typography variant="h6" className="title" sx={{ mb: 1 }}>Order Summary</Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="right">Price</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedTests.map(t => (
                                    <TableRow key={t._id}>
                                        <TableCell>{t.testCode}</TableCell>
                                        <TableCell>{t.name}</TableCell>
                                        <TableCell><Chip label="Test" size="small" variant="outlined" /></TableCell>
                                        <TableCell align="right">{formatCurrency(t.price)}</TableCell>
                                    </TableRow>
                                ))}
                                {selectedPanels.map(p => (
                                    <TableRow key={p._id}>
                                        <TableCell>{p.panelCode}</TableCell>
                                        <TableCell>{p.name}</TableCell>
                                        <TableCell><Chip label="Panel" size="small" color="primary" variant="outlined" /></TableCell>
                                        <TableCell align="right">{formatCurrency(p.price)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', pr: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            Total: {formatCurrency(orderTotal)}
                        </Typography>
                    </Box>
                </Paper>
            )}

            <Box sx={{ display: "flex", flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 3, justifyContent: { xs: 'center', sm: 'right' } }}>
                <Button className="commonBtn" type="submit" variant="contained" disabled={loading}>
                    {requireConsent ? 'Review & Consent' : 'Place Order'}
                </Button>
                <Button variant="outlined" className="btn cancelBtn" onClick={() => navigate(`${basePath}/laboratory/orders`)}>Cancel</Button>
            </Box>

            {/* Review & consent dialog — patient reviews all filled fields,
                tests and total cost, then signs (lis.orderConsent). */}
            <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Review & Patient Consent</DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Patient Details</Typography>
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                        <Grid item xs={6}><Typography variant="body2"><strong>Name:</strong> {selectedPatient?.fName} {selectedPatient?.lName}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="body2"><strong>MRN:</strong> {selectedPatient?.mrn || '-'}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="body2"><strong>DOB:</strong> {formatDob(selectedPatient?.dob)}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="body2" sx={{ textTransform: 'capitalize' }}><strong>Gender:</strong> {selectedPatient?.gender || '-'}</Typography></Grid>
                        <Grid item xs={12}><Typography variant="body2"><strong>Address:</strong> {patientAddress || '-'}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="body2"><strong>Phone:</strong> {patientPhone || '-'}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="body2" sx={{ textTransform: 'capitalize' }}><strong>Priority:</strong> {reviewData?.priority}</Typography></Grid>
                        {reviewData?.orderingPhysicianName && (
                            <Grid item xs={12}>
                                <Typography variant="body2">
                                    <strong>Physician:</strong> {reviewData.orderingPhysicianName}
                                    {reviewData.orderingPhysicianPhone ? ` (${reviewData.orderingPhysicianPhone})` : ''}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Tests & Costs</Typography>
                    <Table size="small" sx={{ mb: 1 }}>
                        <TableBody>
                            {selectedTests.map(t => (
                                <TableRow key={t._id}>
                                    <TableCell sx={{ py: 0.5 }}>{t.testCode} — {t.name}</TableCell>
                                    <TableCell align="right" sx={{ py: 0.5 }}>{formatCurrency(t.price)}</TableCell>
                                </TableRow>
                            ))}
                            {selectedPanels.map(p => (
                                <TableRow key={p._id}>
                                    <TableCell sx={{ py: 0.5 }}>{p.panelCode} — {p.name}</TableCell>
                                    <TableCell align="right" sx={{ py: 0.5 }}>{formatCurrency(p.price)}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(orderTotal)}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        I confirm that the demographic details above are correct, and I consent to the
                        collection and use of this information and to the scheduled tests at the costs shown.
                    </Typography>
                    <TextField
                        fullWidth size="small" label="Signed by (patient / guardian)" sx={{ mb: 1.5 }}
                        value={consentSignedBy} onChange={(e) => setConsentSignedBy(e.target.value)}
                    />
                    <SignaturePad onChange={setConsentSignature} label="Patient signature" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReviewOpen(false)}>Back</Button>
                    <Button variant="contained" onClick={confirmConsentAndSubmit} disabled={loading || !consentSignature || !consentSignedBy.trim()}>
                        {loading ? <CircularProgress size={20} /> : 'Confirm & Place Order'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Quick-create patient dialog */}
            <AddPatientDialog
                open={addPatientOpen}
                onClose={() => setAddPatientOpen(false)}
                onPatientCreated={(patient) => {
                    setSelectedPatient(patient);
                    setPatientOptions([]);
                }}
            />
        </Box>
    );
};

export default LabOrderForm;
